/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { UserDataService } from '@shared/services/user-data.service';
import { UtilsService } from '@shared/services/utils.service';
import { ServerResponseModel } from '@shared/models/serverResponse.model';
import { SignupUser } from '@shared/models/signupUser.model';
import { Constants } from '@shared/models/constants.model';

@Injectable({ providedIn: 'root' })
export class SignupService {
  constructor(
    private httpClient: HttpClient,
    private userDataService: UserDataService,
    private utilsService: UtilsService
  ) {}

  checkUserExists(payload): Observable<any> {
    return this.httpClient.post(`${environment.apiHostname}/v1/users/search`, payload);
  }

  checkEmailValid(email: string): Observable<any> {
    return this.httpClient.get(`${environment.apiHostname}/v1/users/validate/${email}`);
  }

  prepareInviteeAccount(formData: any, purchaseType: string, createAccountOnTP = false) {
    const email = formData.email.trim().toLowerCase();
    return this.checkUserExists({ email }).pipe(
      switchMap((result) => {
        // createAccountOnTP is set when we want to create the athlete even if he is invited to a Team Package
        // this is used when the parent invites the athlete
        if (purchaseType === Constants.PURCHASE_TYPE.ONE_EVENT_TP && !createAccountOnTP) {
          /* FOR TEAM PACKAGE INVITES: lookup for existing account */
          return result?.data?.length ? of({ data: result.data[0] }) : of(null);
        } else {
          /* FOR NON-TEAM_PACKAGE INVITES create account or update existing one to athlete */
          /* FOR TEAM PACKAGE INVITES that were sent by a parent, precreate the user account*/
          if (result?.data?.length) {
            // update account to athlete
            return this.prepareAthleteAccount(email);
          } else {
            // no account
            return this.createUnclaimedAccount(formData);
          }
        }
      })
    );
  }

  doSignup(payload: SignupUser): Observable<any> {
    return this.httpClient.post(`${environment.apiHostname}/v1/users`, payload).pipe(map(this.handleServerResponse));
  }

  updateUser(payload: any): Observable<any> {
    /* never send password field on update */
    delete payload.password;
    return this.httpClient
      .put(`${environment.apiHostname}/v1/users/${payload.id}`, payload)
      .pipe(map(this.handleServerResponse));
  }

  claimAccount(payload: any): Observable<any> {
    /* never send password field on update */
    return this.httpClient
      .put(`${environment.apiHostname}/v1/users/${payload.id}`, { ...payload, claimed: true })
      .pipe(map(this.handleServerResponse));
  }

  saveToken(payload): void {
    this.userDataService.setToken(payload);
  }

  getSignupUser(): any {
    return this.userDataService.getSignupData();
  }

  saveSignupUser(payload): void {
    this.userDataService.setSignupData(payload);
  }

  clearSignupUser(): void {
    this.userDataService.clearSignupData();
  }

  generateUsernameFromEmail(username) {
    // braintree customer IDs have a maximum character count of 36
    // so we need to only take the first 31 characters from the email
    // 31 + 1 + 4 (random characters) is a maximum of 36 characters
    const slicedString = username.replace(/(@|\.|\+)/gim, '-').slice(0, 31);
    return slicedString + '-' + this.utilsService.generateRandomString(4);
  }

  private prepareAthleteAccount(email: string) {
    return this.httpClient.put(`${environment.apiHostname}/v1/users/prepare-invitee/${email}`, {});
  }

  private createUnclaimedAccount(payload: {
    email: string;
    firstName: string;
    lastName: string;
    graduationYear: string;
  }) {
    return this.httpClient.post(`${environment.apiHostname}/v1/users/unclaimed-profile`, payload);
  }

  private handleServerResponse = (response: ServerResponseModel) => {
    if (response.result.success) {
      return response.data;
    } else {
      throw new Error(response.result.message);
    }
  };
}
